import { SplitTypeEnum } from "@finway-group/shared/lib/models/expense/splitType.enum"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils"
import { Button, Card, Table, Tabs } from "antd"
import { ColumnsType } from "antd/es/table"
import React from "react"
import { useTranslation } from "react-i18next"
import { generatePath, useHistory } from "react-router-dom"
import { PurchaseOrderIcon } from "~Components/icons/PurchaseOrderIcon"
import { PurchaseOrderLinkModal } from "~Components/modals/PurchaseOrderLink/PurchaseOrderLinkModal"
import { PurchaseOrderStatusTag } from "~Features/pages/purchaseOrders/components/PurchaseOrderStatusTag"
import { usePurchaseOrdersForLinkingQuery } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { type PurchaseOrder } from "~Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { useModal } from "~Shared/context/modal.context"
import { useExpense } from "~Shared/queries/expenses/expense.queries"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { formatDate } from "~Shared/utils/date.utils"
import { getLinkedPurchaseOrders } from "~Shared/utils/expense.utils"

const { TabPane } = Tabs

export const columns: ColumnsType<PurchaseOrder> = [
    {
        key: "icon",
        width: 50,
        render: () => <PurchaseOrderIcon className="text-blue-500" />,
    },
    {
        key: "nameDescription",
        width: 250,
        ellipsis: true,
        render: (_, { purchaseOrderNumber, description }) => (
            <div className="flex flex-col">
                <span className="font-medium">{purchaseOrderNumber}</span>
                <span className="text-gray-500 truncate">{description}</span>
            </div>
        ),
    },
    {
        key: "createdAt",
        width: 120,
        render: (_, { dateOfIssue }) => formatDate(dateOfIssue),
    },
    {
        key: "totalPrice",
        width: 120,
        render: (_, { totalGrossPrice, currency }) => <span className="font-semibold">{formatCurrencyNumber({ value: totalGrossPrice, currency })}</span>,
    },
    {
        key: "state",
        width: 100,
        render: (_, { state }) => <PurchaseOrderStatusTag status={state?.currentState} />,
    },
]

interface LinkedPurchaseOrdersProps {
    expenseId: string
}
export const LinkedPurchaseOrdersSection = ({ expenseId }: LinkedPurchaseOrdersProps) => {
    const { t } = useTranslation()
    const { data: expense } = useExpense(expenseId)
    const history = useHistory()
    const uniquePurchaseOrders = getLinkedPurchaseOrders(expense)
    const uniqueLinkedPurchaseOrderIds = uniquePurchaseOrders.map((purchaseOrder) => purchaseOrder._id)
    const enabled = uniqueLinkedPurchaseOrderIds.length > 0
    const { data } = usePurchaseOrdersForLinkingQuery(
        {
            filter: { _id: { in: uniqueLinkedPurchaseOrderIds } },
        },
        { enabled, keepPreviousData: false },
    )
    const { docs: purchaseOrders } = data || { docs: [] }
    const { showModal } = useModal()
    // Only show for line item expenses
    if (expense?.splitType !== SplitTypeEnum.LINE_ITEM || expense?.splits?.length === 0) {
        return null
    }

    const handleManageLinks = () => {
        showModal(PurchaseOrderLinkModal, true, {
            isShowing: true,
            expense,
            selectedPurchaseOrders: uniquePurchaseOrders,
        })
    }
    return (
        <Card className="w-full">
            <div className="mb-10 flex justify-between">
                <h3 className="text-lg font-semibold mb-0">{t("label:linked_to_this_invoice")}</h3>
                <Button type="primary" onClick={handleManageLinks} disabled={!enabled}>
                    {t("action:po_link.manage_links")}
                </Button>
            </div>

            <Tabs defaultActiveKey="purchase-orders" className="mt-10">
                <TabPane tab={t("label:purchase_orders")} key="purchase-orders" className="overflow-x-scroll">
                    <Table
                        columns={columns}
                        dataSource={purchaseOrders}
                        rowKey="_id"
                        pagination={false}
                        size="middle"
                        showHeader={false}
                        onRow={(record) => {
                            const hasReadAccess = record?.metadata?.userAccess?.read
                            return {
                                onClick: () => {
                                    if (hasReadAccess) {
                                        history.push(generatePath(RoutePathEnum.PURCHASE_ORDER_DETAILS_PAGE, { id: record._id }))
                                    }
                                },
                                style: {
                                    cursor: hasReadAccess ? "default" : "not-allowed",
                                },
                            }
                        }}
                        className="w-full"
                    />
                </TabPane>
            </Tabs>
        </Card>
    )
}
